import { useMemo } from 'react'
import { Box, Wrap, WrapItem, Button, Text, Stack } from '@chakra-ui/react'
import { FiX } from 'react-icons/fi'
import { useTags } from '../../hooks/useTag'
import { TagPicker } from '../tags/TagPicker'
import type { TagSummaryDto } from '../../types/tag'

export interface TagBucketValue {
  includeTagIds: string[]
  excludeTagIds: string[]
}

interface TagBucketFilterProps {
  value: TagBucketValue
  onChange: (next: TagBucketValue) => void
}

/**
 * "Do's & don'ts" tag filter (L15). Two tap-driven buckets over the same tag roster:
 *   - Must have all of these  -> ContactSearchRequest.includeTagIds (AND-per-tag)
 *   - Exclude any of these    -> ContactSearchRequest.excludeTagIds (NOT-any)
 * A tag already chosen in either bucket is filtered out of both pickers, so a tag can
 * never be both a "do" and a "don't" (mutual exclusivity by construction). No drag-and-drop —
 * add via the searchable TagPicker popover, remove by tapping the chip. `assignableOnly={false}`
 * because a filter operates over tags contacts already carry, not only the manually-assignable set.
 */
export function TagBucketFilter({ value, onChange }: TagBucketFilterProps) {
  const { data: tags } = useTags()
  const { includeTagIds, excludeTagIds } = value

  const labelById = useMemo(
    () => new Map((tags ?? []).map((t) => [t.id, t.label] as const)),
    [tags],
  )
  const chosen = useMemo(
    () => [...includeTagIds, ...excludeTagIds],
    [includeTagIds, excludeTagIds],
  )

  const bucket = (
    ids: string[],
    onAdd: (tag: TagSummaryDto) => void,
    onRemove: (id: string) => void,
    label: string,
    palette: string,
    triggerLabel: string,
  ) => (
    <Box flex="1" minW="220px">
      <Text fontSize="sm" color="ink.2" mb={1}>{label}</Text>
      <Wrap gap={2} align="center">
        {ids.map((id) => (
          <WrapItem key={id}>
            <Button
              type="button"
              size="sm"
              minH="44px"
              borderRadius="full"
              variant="solid"
              colorPalette={palette}
              aria-label={`Remove ${labelById.get(id) ?? 'tag'}`}
              onClick={() => onRemove(id)}
            >
              {labelById.get(id) ?? '…'} <FiX />
            </Button>
          </WrapItem>
        ))}
        <WrapItem>
          <TagPicker
            excludeTagIds={chosen}
            onSelect={onAdd}
            triggerLabel={triggerLabel}
            assignableOnly={false}
          />
        </WrapItem>
      </Wrap>
    </Box>
  )

  const addInclude = (tag: TagSummaryDto) =>
    onChange({ includeTagIds: [...includeTagIds, tag.id], excludeTagIds })
  const addExclude = (tag: TagSummaryDto) =>
    onChange({ includeTagIds, excludeTagIds: [...excludeTagIds, tag.id] })
  const removeInclude = (id: string) =>
    onChange({ includeTagIds: includeTagIds.filter((x) => x !== id), excludeTagIds })
  const removeExclude = (id: string) =>
    onChange({ includeTagIds, excludeTagIds: excludeTagIds.filter((x) => x !== id) })

  return (
    <Box>
      <Text fontSize="sm" color="ink.2" fontWeight="medium" mb={2}>Tags</Text>
      <Stack direction={{ base: 'column', md: 'row' }} gap={6} align="stretch">
        {bucket(includeTagIds, addInclude, removeInclude, 'Must have all of these', 'green', 'Add must-have')}
        {bucket(excludeTagIds, addExclude, removeExclude, 'Exclude any of these', 'red', 'Add exclude')}
      </Stack>
    </Box>
  )
}
